/* ==========================================================================
   Entrance reveals

   Collection grid cards and carousel items rise in with a short GSAP stagger
   as they scroll into view. GSAP loads at the end of <body>, so the
   animation starts on DOMContentLoaded.

   Loaded in <head>, render-blocking on purpose: the `reveal-ready` class
   must be on <html> before cards render, or they'd flash visible and then
   hide.

   Skipped for prefers-reduced-motion, and on back/forward navigation, so
   returning to a page shows it as it was. (Page transitions used to live
   here too; they were removed, so pages load normally.)
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ------------------------------------------------------------------------
  // 2. Entrance reveals
  // ------------------------------------------------------------------------

  var navEntry = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  var backForward = navEntry && navEntry.type === 'back_forward';

  if (reduceMotion || backForward || !('IntersectionObserver' in window)) return;

  // Hides revealable items before first paint (see application.css.liquid).
  root.classList.add('reveal-ready');

  // A page restored from the back/forward cache keeps its DOM, so make sure
  // nothing is left hidden.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) root.classList.remove('reveal-ready');
  });

  // Homepage only in this version (sections/homepage-template.liquid): the
  // feature banners' text, the Explore heading and the In Cinemas text. The
  // intro and the Explore cards have their own animations
  // (homepage-motion.js). These get the gentle version of the reveal.
  var ON_DEMAND = [
    'body.template-index .hero_text_container h4',
    'body.template-index .explore-section > h3',
    'body.template-index .cinemas-upsell-section-text'
  ].join(', ');

  var ITEMS = ON_DEMAND;

  function show(items) {
    items.forEach(function (item) { item.classList.add('is-revealed'); });
  }

  function animate(items) {
    if (!items.length) return;
    show(items);
    var gentle = items[0].matches(ON_DEMAND);
    gsap.fromTo(items,
      { opacity: 0, y: gentle ? 8 : 28 },
      {
        opacity: 1,
        y: 0,
        duration: gentle ? 0.6 : 0.7,
        ease: gentle ? 'power2.out' : 'power3.out',
        stagger: gentle ? 0.05 : 0.06,
        overwrite: true,
        clearProps: 'transform,opacity'
      });
  }

  // Items that enter view together animate as one staggered batch, in
  // document order, rather than each on its own.
  var pending = [];
  var flushQueued = false;

  function flush() {
    flushQueued = false;
    var batch = pending.sort(function (a, b) {
      return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
    pending = [];
    animate(batch);
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      if (!entry.target.classList.contains('is-revealed')) pending.push(entry.target);
    });
    if (pending.length && !flushQueued) {
      flushQueued = true;
      setTimeout(flush, 30);
    }
  }, { rootMargin: '0px 0px -8% 0px' });

  // Exposed so content added later (the fetched recommendations) can join in.
  window.pdpReveal = function (scope) {
    var items = (scope || document).querySelectorAll(ITEMS);
    Array.prototype.forEach.call(items, function (item) {
      if (item.classList.contains('is-revealed')) return;
      // Cards scrolled sideways out of a carousel just show; there's nothing
      // to watch them enter.
      var box = item.getBoundingClientRect();
      if (box.left >= window.innerWidth || box.right <= 0) {
        show([item]);
        return;
      }
      observer.observe(item);
    });
  };

  document.addEventListener('DOMContentLoaded', function () {
    if (typeof gsap === 'undefined') {
      root.classList.remove('reveal-ready');
      return;
    }
    // After the page's own DOMContentLoaded scripts, which on film pages
    // clone carousel cards to loop them; the clones need to be seen too.
    setTimeout(function () { window.pdpReveal(document); }, 0);
  });
})();
